import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from './UserProvider';
import { userAxios, setupAxiosInterceptors } from '../userAxios';

const API_URL = process.env.REACT_APP_API_URL;
const REACT_APP_URL = process.env.REACT_APP_URL;

export const NotificationContext = createContext('');

const NotificationProvider = ({ children }) => {
  const navigate = useNavigate();
  const { token, handleInvalidToken } = useContext(UserContext);
  const [filters, setFilters] = useState({
    comments: false,
    uploads: false,
    websites: false,
    accounts: false,
  });
  const dropdownRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setupAxiosInterceptors(handleInvalidToken);
  }, [handleInvalidToken]);

  useEffect(() => {
    const handleNewNotification = (event) => {
      const newNotification = event.detail;
      setNotifications((prevNotifications) => [
        ...prevNotifications,
        newNotification,
      ]);
    };

    const handleUpdateNotifications = (event) => {
      const updatedNotifications = event.detail;
      setNotifications(updatedNotifications);
    };

    window.addEventListener('new_notification', handleNewNotification);
    window.addEventListener('update_notifications', handleUpdateNotifications);

    return () => {
      window.removeEventListener('new_notification', handleNewNotification);
      window.removeEventListener(
        'update_notifications',
        handleUpdateNotifications
      );
    };
  }, []);

  const toggleDropdownMenu = () => {
    if (dropdownRef.current) {
      dropdownRef.current.classList.toggle('show');
    }
  };

  const getFilteredNotifications = () => {
    return filterNotifications(notifications);
  };

  const viewNotification = async (notification) => {
    try {
      await userAxios.put(
        `${API_URL}/notifications/${notification.rowKey}/view`,
        '',
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setRefreshKey(refreshKey + 1);
      window.open(`${REACT_APP_URL}/${notification.url}`, '_blank');
    } catch (err) {
      console.error(err);
      navigate('/internal-error');
    }
  };

  const getAllNotifications = async () => {
    try {
      const response = await userAxios.get(`${API_URL}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (err) {
      navigate('/internal-error');
    }
  };

  const filterNotifications = (notifications) => {
    if (
      filters.comments ||
      filters.uploads ||
      filters.websites ||
      filters.accounts
    ) {
      return notifications.filter((notification) => {
        return (
          (filters.comments && notification.category === 'Comments') ||
          (filters.uploads && notification.category === 'Uploads') ||
          (filters.websites && notification.category === 'Websites') ||
          (filters.accounts && notification.category === 'Accounts')
        );
      });
    } else {
      return notifications;
    }
  };

  const dismissSelectedNotifications = async (selectedNotificationIds) => {
    try {
      await userAxios.put(
        `${API_URL}/notifications/dismiss`,
        selectedNotificationIds,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const newNotifications = notifications.filter(
        (notification) => !selectedNotificationIds.includes(notification.rowKey)
      );
      setNotifications(newNotifications);
      setRefreshKey(refreshKey + 1);
    } catch (err) {
      console.error(err);
      navigate('/internal-error');
    }
  };

  const dismissAllNotifications = async () => {
    const allNotificationIds = notifications.map(
      (notification) => notification.rowKey
    );
    await dismissSelectedNotifications(allNotificationIds);
    setRefreshKey(refreshKey + 1);
  };

  const removeNotification = async (notification) => {
    try {
      await userAxios.put(
        `${API_URL}/notifications/${notification.rowKey}/dismiss`,
        '',
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const newNotifications = notifications.filter(
        (n) => n.rowKey !== notification.rowKey
      );
      setNotifications(newNotifications);
      setRefreshKey(refreshKey + 1);
    } catch (err) {
      console.error(err);
    }
  };

  const deleteNotification = (notificationToDelete) => {
    if (notificationToDelete) {
      removeNotification(notificationToDelete);
    } else {
      dismissAllNotifications();
    }
  };

  const iconMap = {
    success: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="h-6 w-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    error: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="h-6 w-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
        />
      </svg>
    ),
    info: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="h-6 w-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12v-.008z"
        />
      </svg>
    ),
  };

  return (
    <NotificationContext.Provider
      value={{
        filters,
        setFilters,
        notifications,
        setNotifications,
        refreshKey,
        setRefreshKey,
        iconMap,
        toggleDropdownMenu,
        getFilteredNotifications,
        getAllNotifications,
        filterNotifications,
        dismissSelectedNotifications,
        dismissAllNotifications,
        removeNotification,
        deleteNotification,
        viewNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
